<template>
  <div class="space-y-8">
    <!-- Compte -->
    <section class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="font-semibold text-gray-900 mb-4">Compte partenaire</h3>
      <div class="grid md:grid-cols-2 gap-4 text-sm">
        <div>
          <p class="text-gray-500">Entreprise</p>
          <p class="font-medium text-gray-900">{{ authStore.partner?.name }}</p>
        </div>
        <div>
          <p class="text-gray-500">Statut</p>
          <span
            :class="[
              'inline-block px-3 py-1 rounded-full text-xs font-semibold mt-0.5',
              authStore.isSuspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            ]"
          >
            {{ authStore.isSuspended ? 'Suspendu' : 'Actif' }}
          </span>
        </div>
        <div>
          <p class="text-gray-500">Identifiant partenaire</p>
          <code class="text-xs font-mono text-gray-700">{{ authStore.partner?.id }}</code>
        </div>
        <div>
          <p class="text-gray-500">Votre rôle</p>
          <p class="font-medium text-gray-900">
            {{ authStore.isOwner ? 'Propriétaire' : 'Développeur' }}
          </p>
        </div>
      </div>
    </section>

    <!-- Facturation -->
    <section class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="font-semibold text-gray-900 mb-1">Facturation</h3>
      <p class="text-sm text-gray-500 mb-4">
        Mode actuel :
        <strong class="text-gray-900">{{ billingLabel(authStore.partner?.billingMode) }}</strong>
      </p>

      <div
        v-if="authStore.partner?.requestedBillingMode"
        class="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900"
      >
        <p>
          Votre demande de passage en
          <strong>{{ billingLabel(authStore.partner.requestedBillingMode) }}</strong>
          est en cours de validation par l'équipe Youdégo. Le mode actuel reste actif jusqu'à approbation.
        </p>
      </div>

      <p v-else class="text-sm text-gray-600">
        Les modes wallet prépayé et facture mensuelle arriveront dans une prochaine mise à jour du portail.
        Contactez le support Youdégo pour en discuter dès maintenant.
      </p>
    </section>

    <!-- Équipe -->
    <section class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="font-semibold text-gray-900">Équipe</h3>
          <p class="text-sm text-gray-500">
            Les développeurs accèdent aux clés, aux webhooks, aux livraisons et à la documentation.
          </p>
        </div>
        <button
          v-if="authStore.isOwner && !authStore.isSuspended"
          @click="showModal = true"
          class="px-4 py-2 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 flex items-center gap-2 flex-shrink-0"
        >
          <Icon icon="solar:user-plus-bold" class="w-4 h-4" />
          Inviter un développeur
        </button>
      </div>

      <div class="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        <div v-if="loading" class="p-4 animate-pulse">
          <div class="h-4 bg-gray-100 rounded w-1/3"></div>
        </div>
        <div
          v-else
          v-for="m in members"
          :key="m.id"
          class="flex items-center justify-between p-4 hover:bg-gray-50"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Icon icon="solar:user-bold" class="w-4 h-4 text-gray-600" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ m.user.firstname }} {{ m.user.lastname }}
              </p>
              <p class="text-xs text-gray-500">{{ m.user.phone }}</p>
            </div>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <span
              :class="[
                'px-3 py-1 rounded-full text-xs font-semibold',
                m.role === 'OWNER' ? 'bg-[#8C0004] bg-opacity-10 text-[#8C0004]' : 'bg-gray-100 text-gray-700'
              ]"
            >
              {{ m.role === 'OWNER' ? 'Propriétaire' : 'Développeur' }}
            </span>
            <button
              v-if="authStore.isOwner && m.role !== 'OWNER'"
              @click="removeMember(m)"
              class="text-xs text-[#8C0004] hover:underline"
            >
              Retirer
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Modal invitation -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="showModal = false"
    >
      <div class="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div class="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-lg font-bold text-gray-900">Inviter un développeur</h3>
          <button @click="showModal = false" class="text-gray-400 hover:text-gray-600">
            <Icon icon="solar:close-circle-bold" class="w-6 h-6" />
          </button>
        </div>
        <form @submit.prevent="addMember" class="p-6 space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
              <input
                v-model="form.firstname"
                required
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Nom</label>
              <input
                v-model="form.lastname"
                required
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Numéro WhatsApp</label>
            <input
              v-model="form.phone"
              maxlength="10"
              placeholder="0XXXXXXXXX"
              required
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
            />
            <p class="text-xs text-gray-500 mt-2">
              Il se connectera au portail avec ce numéro, par code WhatsApp.
            </p>
          </div>

          <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-sm text-red-600">{{ error }}</p>
          </div>

          <div class="flex gap-3 pt-2">
            <button
              type="button"
              @click="showModal = false"
              class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="flex-1 px-4 py-2.5 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50"
            >
              {{ saving ? 'Ajout...' : 'Inviter' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import { authClient } from '../services/api'
import { useAuthStore } from '../store/auth'
import { useNotification } from '../composables/useNotification'

const authStore = useAuthStore()
const notify = useNotification()

const members = ref<any[]>([])
const loading = ref(true)
const showModal = ref(false)
const saving = ref(false)
const error = ref('')

const form = ref({ firstname: '', lastname: '', phone: '' })

onMounted(load)

// Les comptes sont gérés côté production (source de vérité) puis mirroités
// vers l'environnement sandbox : cet écran parle donc toujours à la prod.
async function load() {
  loading.value = true
  try {
    const { data } = await authClient.get('/partner-portal/members')
    members.value = data.members || []
  } catch (err) {
    console.error('Erreur chargement membres:', err)
    members.value = []
  } finally {
    loading.value = false
  }
}

async function addMember() {
  saving.value = true
  error.value = ''
  try {
    await authClient.post('/partner-portal/members', form.value)
    showModal.value = false
    form.value = { firstname: '', lastname: '', phone: '' }
    notify.success('Développeur ajouté')
    await load()
  } catch (err: any) {
    error.value = err.response?.data?.message || "Erreur lors de l'ajout"
  } finally {
    saving.value = false
  }
}

async function removeMember(member: any) {
  if (
    !window.confirm(
      `Retirer ${member.user.firstname} ${member.user.lastname} de l'équipe ? Son accès au portail sera immédiatement coupé.`
    )
  ) {
    return
  }
  try {
    await authClient.delete(`/partner-portal/members/${member.id}`)
    notify.success('Membre retiré')
    await load()
  } catch (err: any) {
    notify.error(err.response?.data?.message || 'Erreur')
  }
}

function billingLabel(mode?: string | null): string {
  return (
    {
      PER_DELIVERY: 'Paiement à la course',
      PREPAID_WALLET: 'Wallet prépayé',
      MONTHLY_INVOICE: 'Facture mensuelle',
    } as Record<string, string>
  )[mode || ''] || '—'
}
</script>
